import { NsnReport, PartFinderImageKind, PartRelationshipGraph, PartRelationshipNodeType } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useGetPartRelationshipGraph } from '@/hooks/api/part-finder-reports/useGetPartRelationshipGraph';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { getApiError } from '@/types/apiError';
import { HubOutlined } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { CollapsibleSection } from '../CollapsibleSection';
import { EndItemChain } from './EndItemChain';
import { PhraseLinksTable } from './PhraseLinksTable';
import {
  describeComponents,
  describeEvidence,
  describeGraphSize,
  EDGE_LABELS,
  filterRelationshipGraph,
  focusOnNode,
  hasNeighborsBeyondCatalogIdentity,
  RELATIONSHIP_FILTERS,
  RelationshipFilter,
} from './relationshipGraphLayout';
import { RelationshipGraphCanvas } from './RelationshipGraphCanvas';
import { nodeTypeCaption, nodeTypeColor } from './RelationshipNode';
import { NodeMenuAnchor, RelationshipNodeMenu } from './RelationshipNodeMenu';

const Legend = () => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {Object.values(PartRelationshipNodeType).map(type => (
        <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: nodeTypeColor(theme, type) }} />
          <Typography variant='caption' color='text.secondary'>
            {nodeTypeCaption(type)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const EvidenceLine = ({ graph, hoveredEdgeId }: { graph: PartRelationshipGraph; hoveredEdgeId?: string }) => {
  const edge = graph.edges.find(candidate => candidate.id === hoveredEdgeId);
  return (
    <Typography variant='caption' color='text.secondary' component='div' data-testid='relationship-evidence'>
      {edge
        ? `${EDGE_LABELS[edge.type]}: ${describeEvidence(edge)}`
        : 'Hover a line to see its evidence. Click a node for actions.'}
    </Typography>
  );
};

const GraphFooter = ({
  graph,
  hoveredEdgeId,
  showsThumbnails,
}: {
  graph: PartRelationshipGraph;
  hoveredEdgeId?: string;
  showsThumbnails: boolean;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
    <EvidenceLine graph={graph} hoveredEdgeId={hoveredEdgeId} />
    <Typography variant='caption' color='text.secondary' data-testid='relationship-graph-size'>
      {showsThumbnails && 'Thumbnails from saved reports, images © WBParts · '}
      {describeGraphSize(graph)}
    </Typography>
  </Box>
);

const GraphBody = ({ graph, filter }: { graph: PartRelationshipGraph; filter: RelationshipFilter }) => {
  const { isPartFinderThumbnailsEnabled } = useFeatureFlags();
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string>();
  const [focusedNodeId, setFocusedNodeId] = useState<string>();
  const [menuAnchor, setMenuAnchor] = useState<NodeMenuAnchor>();
  const visible = focusOnNode(filterRelationshipGraph(graph, filter), focusedNodeId);
  const focusedNode = graph.nodes.find(node => node.id === focusedNodeId);
  const toggleFocus = (nodeId: string) => setFocusedNodeId(current => (current === nodeId ? undefined : nodeId));
  const componentSummary = describeComponents(graph);
  return (
    <>
      {componentSummary && (
        <Typography variant='body2' sx={{ marginBottom: '8px' }} data-testid='relationship-graph-components'>
          {componentSummary} (parts whose FLIS end item is this NSN)
        </Typography>
      )}
      {!hasNeighborsBeyondCatalogIdentity(graph) && (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ marginBottom: '8px' }}
          data-testid='relationship-graph-minimal'
        >
          FLIS lists no related NSNs or end items for this part, and this report has no saved suppliers. Only its part
          numbers and CAGEs are shown.
        </Typography>
      )}
      {focusedNode && (
        <Alert
          severity='info'
          sx={{ marginBottom: '8px' }}
          action={
            <Button color='inherit' size='small' onClick={() => setFocusedNodeId(undefined)}>
              Show all
            </Button>
          }
        >
          Showing only {focusedNode.label} and its direct links.
        </Alert>
      )}
      <RelationshipGraphCanvas
        key={`${filter}-${focusedNodeId ?? 'all'}`}
        nodes={visible.nodes}
        edges={visible.edges}
        seedNodeId={graph.seedNodeId}
        hoveredEdgeId={hoveredEdgeId}
        onHoverEdge={setHoveredEdgeId}
        thumbnailsEnabled={isPartFinderThumbnailsEnabled}
        onSelectNode={(node, position) => setMenuAnchor({ node, ...position })}
      />
      <RelationshipNodeMenu
        anchor={menuAnchor}
        seedNodeId={graph.seedNodeId}
        focusedNodeId={focusedNodeId}
        onClose={() => setMenuAnchor(undefined)}
        onToggleFocus={toggleFocus}
      />
      <GraphFooter
        graph={graph}
        hoveredEdgeId={hoveredEdgeId}
        showsThumbnails={
          isPartFinderThumbnailsEnabled &&
          graph.nodes.some(node => node.hasThumbnail && node.thumbnailKind !== PartFinderImageKind.SearchPhoto)
        }
      />
    </>
  );
};

const RelatedPartsGraph = ({ reportId }: { reportId: string }) => {
  const [filter, setFilter] = useState<RelationshipFilter>('all');
  const { data: graph, isLoading, error } = useGetPartRelationshipGraph(reportId);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <TextField
          select
          size='small'
          label='Show'
          value={filter}
          onChange={event => setFilter(event.target.value as RelationshipFilter)}
          sx={{ minWidth: 200 }}
        >
          {RELATIONSHIP_FILTERS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Legend />
      </Box>
      {isLoading && <CircularProgress size={24} />}
      <ApiErrorAlert error={getApiError(error)} />
      {graph && <GraphBody key={filter} graph={graph} filter={filter} />}
    </>
  );
};

export const RelatedPartsSection = ({
  reportId,
  nsnReport,
  showGraph,
}: {
  reportId: string;
  nsnReport: NsnReport;
  showGraph: boolean;
}) => (
  <CollapsibleSection icon={<HubOutlined />} title='Related Parts' defaultExpanded={false}>
    <Typography variant='body2' color='text.secondary' sx={{ marginBottom: '8px' }}>
      How this part connects to other catalog entries, computed from FLIS, this report&apos;s suppliers, and the NSN-Now
      contract awards saved with it. An end item is the finished equipment a part ends up in, like a tank, a truck, or
      an aircraft. FLIS lists it as free text, so end items and shared part numbers are leads, not proof of
      interchangeability or a bill of materials.
    </Typography>
    <EndItemChain nsnReport={nsnReport} />
    {!!nsnReport.phraseLinks?.length && <PhraseLinksTable phraseLinks={nsnReport.phraseLinks} />}
    {showGraph && <RelatedPartsGraph key={reportId} reportId={reportId} />}
  </CollapsibleSection>
);
